import React, { useEffect, useRef, useState } from 'react';

/**
 * RussianSideRails - Wide Skyscraper & Skyscraper Side Rail Ads
 * - Left side: Wide Skyscraper (160x600, key: f1113cc40ae406b75afb8279bb6bfc99)
 * - Right side: Skyscraper / Half-Skyscraper (160x300, key: 92fa16bcfd549b2987bb822b5bad354a)
 *
 * NOTE:
 * 1. Both scripts depend on the global `window.atOptions` variable.
 *    To prevent race conditions, ads are loaded sequentially (Left, then Right).
 * 2. Side rails disappear 7 seconds after the user scrolls the page.
 */
const RussianSideRails = () => {
    const leftRef = useRef(null);
    const rightRef = useRef(null);
    const [leftDismissed, setLeftDismissed] = useState(false);
    const [rightDismissed, setRightDismissed] = useState(false);
    const [autoDismissed, setAutoDismissed] = useState(false);
    const scrollTimerRef = useRef(null);
    const scrollTriggeredRef = useRef(false);

    // Disappear 7 seconds after the user scrolls the page
    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY || window.pageYOffset || 0;
            if (scrollY > 20 && !scrollTriggeredRef.current) {
                scrollTriggeredRef.current = true;
                scrollTimerRef.current = setTimeout(() => {
                    setAutoDismissed(true);
                }, 7000);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimerRef.current) {
                clearTimeout(scrollTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const leftContainer = leftRef.current;
        const rightContainer = rightRef.current;
        if (!leftContainer || !rightContainer) return;

        // Step 1: Configure and load Left ad (160x600)
        const loadLeft = () => {
            return new Promise((resolve) => {
                if (leftContainer.querySelector('iframe, script[src*="f1113cc40ae406b75afb8279bb6bfc99"]')) {
                    return resolve();
                }

                window.atOptions = {
                    'key': 'f1113cc40ae406b75afb8279bb6bfc99',
                    'format': 'iframe',
                    'height': 600,
                    'width': 160,
                    'params': {}
                };

                const confScript = document.createElement('script');
                confScript.type = 'text/javascript';
                confScript.text = `
                    atOptions = {
                        'key' : 'f1113cc40ae406b75afb8279bb6bfc99',
                        'format' : 'iframe',
                        'height' : 600,
                        'width' : 160,
                        'params' : {}
                    };
                `;

                const invokeScript = document.createElement('script');
                invokeScript.type = 'text/javascript';
                invokeScript.src = 'https://www.highrevenueformat.com/f1113cc40ae406b75afb8279bb6bfc99/invoke.js';

                let resolved = false;
                const done = () => {
                    if (!resolved) {
                        resolved = true;
                        resolve();
                    }
                };

                invokeScript.onload = () => setTimeout(done, 150);
                invokeScript.onerror = () => done();

                leftContainer.appendChild(confScript);
                leftContainer.appendChild(invokeScript);

                // Fallback timeout in case onload event is delayed
                setTimeout(done, 600);
            });
        };

        // Step 2: Configure and load Right ad (160x300) AFTER Left ad script is loaded and executed
        loadLeft().then(() => {
            if (!rightContainer) return;
            if (rightContainer.querySelector('iframe, script[src*="92fa16bcfd549b2987bb822b5bad354a"]')) {
                return;
            }

            window.atOptions = {
                'key': '92fa16bcfd549b2987bb822b5bad354a',
                'format': 'iframe',
                'height': 300,
                'width': 160,
                'params': {}
            };

            const confScript = document.createElement('script');
            confScript.type = 'text/javascript';
            confScript.text = `
                atOptions = {
                    'key' : '92fa16bcfd549b2987bb822b5bad354a',
                    'format' : 'iframe',
                    'height' : 300,
                    'width' : 160,
                    'params' : {}
                };
            `;

            const invokeScript = document.createElement('script');
            invokeScript.type = 'text/javascript';
            invokeScript.src = 'https://www.highrevenueformat.com/92fa16bcfd549b2987bb822b5bad354a/invoke.js';

            rightContainer.appendChild(confScript);
            rightContainer.appendChild(invokeScript);
        });
    }, []);

    if (autoDismissed) {
        return null;
    }

    return (
        <div className="ru-side-rails-wrapper adsterra-side-rails-wrapper" aria-hidden="false">
            {!leftDismissed && (
                <aside
                    className="ru-side-rail ru-side-rail-left adsterra-side-rail adsterra-side-rail-left"
                    aria-label="Advertisement Left"
                >
                    <div className="ru-side-rail-header adsterra-side-rail-header">
                        <span className="ru-side-rail-label adsterra-side-rail-label"></span>
                        <button
                            type="button"
                            className="ru-side-rail-close adsterra-side-rail-close"
                            onClick={() => setLeftDismissed(true)}
                            title="Закрыть рекламу"
                            aria-label="Закрыть рекламу"
                        >
                            &times;
                        </button>
                    </div>
                    <div className="ru-side-rail-body adsterra-side-rail-body" ref={leftRef} />
                </aside>
            )}

            {!rightDismissed && (
                <aside
                    className="ru-side-rail ru-side-rail-right adsterra-side-rail adsterra-side-rail-right"
                    aria-label="Advertisement Right"
                >
                    <div className="ru-side-rail-header adsterra-side-rail-header">
                        <span className="ru-side-rail-label adsterra-side-rail-label"></span>
                        <button
                            type="button"
                            className="ru-side-rail-close adsterra-side-rail-close"
                            onClick={() => setRightDismissed(true)}
                            title="Закрыть рекламу"
                            aria-label="Закрыть рекламу"
                        >
                            &times;
                        </button>
                    </div>
                    <div className="ru-side-rail-body adsterra-side-rail-body" ref={rightRef} />
                </aside>
            )}
        </div>
    );
};

export default RussianSideRails;
